/**
 * Whether a workspace's delivery reports reach Wend at all — slice 19's first
 * two done-whens, as a contract.
 *
 * ## WHAT THIS FILE IS FOR: THE LIVE DEPLOYMENT, SENT BLIND
 *
 * The deployment that sends real campaigns has had thousands of emails accepted
 * by Amazon and has never received one delivery report. Slice 20 made the
 * dashboard stop drawing that as good news — `noOutcomeReported` and its copy in
 * `./overview.js` say *no report has come back* instead of printing `0.00%` in
 * green — but it could only hedge about why, because a predicate over one
 * window's counts cannot tell "reports are late" from "reports have never once
 * arrived". So that sentence says *probably*, and gives the reader a rule of
 * thumb.
 *
 * This is the fact the sentence was missing, and it is a fact about the
 * **workspace**, not about a window of numbers:
 *
 *   - **`lastReceivedAt`** — when Wend last stored a report of any kind on this
 *     channel for this workspace, or `null` for never. Null is not "the numbers
 *     are missing"; it is "this workspace's reporting has never worked", and
 *     done-when 1 asks for exactly that to be said, on the Overview and on the
 *     campaign. A date is how reports *stopping* becomes noticeable
 *     (done-when 2) rather than looking like a quiet week;
 *   - **`setup`**, email only — the first step Wend can see is missing, so the
 *     never-received sentence comes with its repair.
 *
 * Received means **stored**: `delivery_events.created_at`, a row the ingest
 * understood and matched to one of this workspace's messages. A subscription
 * confirmation, a forged POST, or a notification about a message Wend never
 * sent is not a delivery report about this workspace's mail, and counting one
 * would say "reports are arriving" over campaigns nobody has reported on.
 *
 * Read through one partial index per provider (migration 0023): the table is
 * partitioned by the month an event *occurred*, and without an index on when
 * one was stored, "the newest, ever" would read every partition the workspace
 * has, on every Overview load.
 *
 * ## WHAT IS NOT HERE
 *
 * No rate and no count. This answers whether the pipe works, never what came
 * through it — that stays in `./overview.js`, measured over a window, where the
 * zero-versus-not-measured rules already live. And the setup state is computed
 * by the API, not here: it needs `configurationSetNameForSend`, which lives in
 * `./engagement.js`, which imports the overview, which imports this file — a
 * cycle between modules that build zod schemas at load.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';
import type { Channel } from '../enums.js';

/* -------------------------------------------------------------------------- */
/* Email setup — the first missing step Wend can see                           */
/* -------------------------------------------------------------------------- */

/**
 * The first step on the way to email delivery reports that Wend can see is
 * missing, **in the order a person sets it up**:
 *
 *   no_public_address   the deployment has no `API_BASE_URL`, so the webhook
 *                       address points at the machine Wend runs on and Amazon
 *                       cannot reach it. First, because every later step builds
 *                       on an address that exists;
 *   no_sending_account  no SES account is connected to this workspace;
 *   no_topic            no SNS topic is saved in Wend, so Amazon has not been
 *                       told where to send this workspace's reports;
 *   topic_not_accepted  a topic is saved, and the deployment's
 *                       `SNS_TOPIC_ARN_ALLOWLIST` does not include it — so Wend
 *                       will not confirm its subscription, and an unconfirmed
 *                       subscription delivers nothing;
 *   not_set_up          no configuration set that reports is named on this
 *                       workspace's sends: delivery reports were never set up,
 *                       or were dropped, and tracking is not carrying them;
 *   ready               everything Wend can check. The one step it cannot —
 *                       whether the subscription reads Confirmed in AWS — is
 *                       what the sentence for this state names.
 *
 * `ready` does not mean reports are arriving. That is `lastReceivedAt`'s job,
 * and the two together are the useful sentence: set up, and still never
 * received, points at the subscription.
 */
export const DELIVERY_REPORTS_SETUP_VALUES = [
  'no_public_address',
  'no_sending_account',
  'no_topic',
  'topic_not_accepted',
  'not_set_up',
  'ready',
] as const;
export const deliveryReportsSetupSchema = z.enum(DELIVERY_REPORTS_SETUP_VALUES);
export type DeliveryReportsSetup = z.infer<typeof deliveryReportsSetupSchema>;

/* -------------------------------------------------------------------------- */
/* The fact, per channel                                                       */
/* -------------------------------------------------------------------------- */

export const emailDeliveryReportsSchema = z
  .object({
    /**
     * When Wend last stored a report from Amazon for this workspace — **any** event
     * Amazon sends about its mail: a delivery, a bounce, a complaint, a delay, a
     * rejection, an open. Null: never. Any of them proves reports reach Wend, which
     * is the question; the screens say "report", not "delivery report", because
     * the newest one may be an open.
     */
    lastReceivedAt: isoDateTimeSchema.nullable(),
    setup: deliveryReportsSetupSchema,
  })
  .strict();
export type EmailDeliveryReports = z.infer<typeof emailDeliveryReportsSchema>;

/**
 * WhatsApp carries no setup state. Meta's status webhook is configured per
 * deployment, in Meta's own dashboard, and nothing on a workspace's row says
 * whether it was — so there is no step Wend could name without guessing.
 */
export const whatsAppDeliveryReportsSchema = z
  .object({
    /** When Wend last stored a status report from Meta for this workspace — any status. Null: never. */
    lastReceivedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type WhatsAppDeliveryReports = z.infer<typeof whatsAppDeliveryReportsSchema>;

export const deliveryReportsSchema = z
  .object({
    email: emailDeliveryReportsSchema,
    whatsapp: whatsAppDeliveryReportsSchema,
  })
  .strict();
export type DeliveryReports = z.infer<typeof deliveryReportsSchema>;

/* -------------------------------------------------------------------------- */
/* The words                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What a workspace that has never received a report is told, per channel.
 *
 * **Flat, not hedged.** `NO_OUTCOME_REPORTED_COPY` says *probably* because it
 * cannot see past one window's counts; this is said only when Wend knows no
 * report has ever been stored, and hedging a certainty is how a reader learns to
 * wait for something that is not coming. `short` names the fact; `detail` says
 * what it costs and that waiting will not fix it. The repair is
 * {@link EMAIL_DELIVERY_REPORTS_SETUP_COPY}'s, printed beside it.
 */
export const DELIVERY_REPORTS_NEVER_COPY: Readonly<
  Record<Channel, { readonly short: string; readonly detail: string }>
> = {
  email: {
    short: 'Wend has never received a delivery report from Amazon for this workspace.',
    detail:
      'So nothing here can say whether any email this workspace has sent was delivered, bounced or marked as spam — for any campaign, not only these. That is a setup fact rather than a wait: once Amazon has somewhere to send reports, they arrive within minutes of a send.',
  },
  whatsapp: {
    short: 'Wend has never received a delivery report from Meta for this workspace.',
    detail:
      'So nothing here can say whether any WhatsApp message this workspace has sent was delivered or failed. That is a setup fact rather than a wait: once Meta’s status updates reach Wend, they arrive within minutes of a send.',
  },
};

/**
 * The label on the button that sets up delivery reports — one spelling, because
 * the API's refusals tell a person to press it by name, and a sentence naming a
 * button the screen does not have is a dead end with a paragraph attached. The
 * web app draws it; the API quotes it.
 */
export const SET_UP_DELIVERY_REPORTS_LABEL = 'Set up delivery reports';

/**
 * The first missing step, as a sentence a person can act on. Total over
 * {@link DeliveryReportsSetup}, so a new state is a compile error here rather
 * than a blank line on the Overview.
 *
 * The two deployment steps name their setting, because the person who reads
 * them on the live deployment is the person who sets it. A hosted install with
 * a public address never reaches the first; the second it reaches for every new
 * workspace, until an operator adds that workspace's topic to the list — so the
 * sentence says the deployment has not been told, which is true either way.
 */
export const EMAIL_DELIVERY_REPORTS_SETUP_COPY: Readonly<Record<DeliveryReportsSetup, string>> = {
  no_public_address:
    'The first missing step is on the Wend deployment itself: it has no public web address (API_BASE_URL), so the webhook address Amazon would report to points at the machine Wend runs on, where Amazon cannot reach it.',
  no_sending_account:
    'The first missing step: no Amazon SES account is connected to this workspace.',
  no_topic:
    'The first missing step: no SNS topic has been saved on the Integrations screen, so Amazon has not been told where to send this workspace’s reports.',
  topic_not_accepted:
    'The first missing step is on the Wend deployment: your SNS topic is saved, but Wend has not been told to accept it (SNS_TOPIC_ARN_ALLOWLIST), so it will not confirm the topic’s subscription — and an unconfirmed subscription delivers nothing.',
  not_set_up:
    'The first missing step: delivery reports are not set up in your AWS account, so your emails do not go through a configuration set that reports on them. Set them up on the Integrations screen.',
  ready:
    'Everything Wend can check is in place. The one step it cannot see is in the SNS console: your topic’s subscription to Wend’s webhook address has to read Confirmed.',
};

/**
 * Beside `NO_OUTCOME_REPORTED_COPY`, when reports **have** arrived before: the
 * pipe has worked, so "probably a setup step" is the wrong first thought. `when`
 * is the formatted `lastReceivedAt` — formatted by the screen, which owns the
 * locale.
 *
 * **Three causes, not one, and the date decides between two of them.** The
 * first version said reports "have stopped reaching Wend". That is one of the
 * things these facts allow, and on the live deployment it would have been the
 * wrong one on every old campaign's page the day reports were set up: email sent
 * before delivery reports existed never gets one, while reports for everything
 * after arrive fine — the last one minutes ago. And the state is only "no
 * delivery or failure reported", which a message a receiving server is still
 * delaying is in too, with its reports arriving perfectly (review). Nothing here
 * knows when these messages went out, or whether they are delayed — the card
 * beside it counts delays — so the sentence names all three and puts the date
 * where it decides: sent before reports were set up, or reports stopped after it.
 *
 * It says "no delivery or bounce", not "no report": the delivery reports set
 * reports a `Send` for every message within seconds, so "none has come back" was
 * false for mail going out perfectly.
 */
export function deliveryReportsReceivedBefore(channel: Channel, when: string): string {
  const provider = channel === 'email' ? 'Amazon' : 'Meta';
  const outcome = channel === 'email' ? 'delivery or bounce' : 'delivery or failure';
  return `Reports from ${provider} have reached Wend — the last one on ${when} — but no ${outcome} has been reported for these. That normally takes minutes, so if these went out more than an hour ago, one of three things is true: a receiving server is still delaying them, and ${provider} will report them when it stops; they were sent before reports were set up, and will never get one; or reports stopped reaching Wend after ${when}.`;
}
